import { PencilSquareIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { PiArrowsMerge } from 'react-icons/pi';
import { formatDateRange } from '../../util/data-factory';
import { TooltipLine, TruncatedText, TOOLTIP_COLORS } from './ui/TooltipCard';
import IconActionButton from './ui/IconActionButton';

function ShowTable({
  rows,
  variant = 'manage',
  showVenueColumn = false,
  onEditShow,
  onHideShow,
  onCombineShow,
  hideAction = 'hide',
  tourTargetShowId,
}) {
  const isHiddenTable = hideAction === 'unhide';
  return (
    <div className="overflow-x-auto">
    <table className="table table-zebra table-fixed w-full">
      <thead>
        <tr>
          <th className="w-[26%] truncate">Show Title</th>
          <th className="w-[15%] truncate">Artist(s)</th>
          <th className="w-[22%] truncate">Dates</th>
          <th className="w-[10%] truncate">{showVenueColumn ? 'Venue' : null}</th>
          {variant === 'manage' ? (
            <th className="w-[120px] text-right truncate">Actions</th>
          ) : (
            <th className="w-[27%] truncate">
              <div className="flex justify-end gap-4">
                <span className="inline-block w-10 text-right whitespace-nowrap text-slate-700">Views</span>
                <span className="inline-block w-10 text-right whitespace-nowrap text-slate-500">Clicks</span>
                <span className="inline-block w-10 text-right whitespace-nowrap text-slate-400">Seen</span>
              </div>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isTourTarget = row.show_id === tourTargetShowId;
          return (
          <tr key={row.show_id}>
            <td className="overflow-visible">
              <TruncatedText>
                <a
                  href={`/show/${row.show_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-red hover:underline cursor-pointer"
                >
                  {row.display_title ?? row.show_title}
                </a>
              </TruncatedText>
            </td>
            <td className="truncate text-sm text-slate-400">
              {(row.artist_names || []).join(', ')}
            </td>
            <td className="truncate text-sm text-slate-400">
              {formatDateRange(row.start_date, row.end_date)}
            </td>
            <td className="overflow-visible text-sm text-slate-400">
              {showVenueColumn && (
                <TruncatedText
                  position="top-left"
                  tooltip={
                    (row.venue_address || row.venue_neighborhood) && (
                      <>
                        {row.venue_address && <TooltipLine>{row.venue_address}</TooltipLine>}
                        {row.venue_neighborhood && (
                          <TooltipLine color={TOOLTIP_COLORS.slate400} weight={400}>
                            {row.venue_neighborhood}
                          </TooltipLine>
                        )}
                      </>
                    )
                  }
                >
                  {row.venue_name}
                </TruncatedText>
              )}
            </td>
            {variant === 'manage' ? (
              <td className="w-[120px] overflow-visible">
                <div className="flex gap-2 justify-end">
                  <IconActionButton
                    label="Edit show"
                    icon={PencilSquareIcon}
                    data-tour={isTourTarget ? 'show-edit-button' : undefined}
                    onClick={() => onEditShow?.(row)}
                  />
                  <IconActionButton
                    label={isHiddenTable ? 'Unhide show' : 'Hide show'}
                    icon={isHiddenTable ? EyeIcon : EyeSlashIcon}
                    tooltipPosition={isHiddenTable ? 'top-right' : undefined}
                    data-tour={isTourTarget ? 'show-hide-button' : undefined}
                    onClick={() => onHideShow?.(row)}
                  />
                  {!isHiddenTable && (
                    <IconActionButton
                      label="Combine with another show"
                      icon={PiArrowsMerge}
                      iconClassName="text-secondary-red -rotate-90"
                      tooltipPosition="top-right"
                      data-tour={isTourTarget ? 'show-combine-button' : undefined}
                      onClick={() => onCombineShow?.(row)}
                    />
                  )}
                </div>
              </td>
            ) : (
              <td className="whitespace-nowrap tabular-nums">
                <div className="flex justify-end gap-4">
                  <span className="inline-block w-10 text-right font-semibold text-slate-700">{row.impressions.toLocaleString()}</span>
                  <span className="inline-block w-10 text-right font-semibold text-slate-500">{row.views.toLocaleString()}</span>
                  <span className="inline-block w-10 text-right font-semibold text-slate-400">{row.seen.toLocaleString()}</span>
                </div>
              </td>
            )}
          </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

export function ShowTableGroup({ title, rows, ...tableProps }) {
  if (rows.length === 0) return null;
  return (
    <div className="px-6">
      <h3 className="text-lg font-semibold text-secondary-red pb-4">{title}</h3>
      <ShowTable rows={rows} {...tableProps} />
    </div>
  );
}

export default ShowTable;
